import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import type { CompraLineaInput, FormaPagoRow, ProductoRow, ProveedorRow } from '../../types/database.types';
import type { LineaCompraBorrador } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

let contadorLocal = 0;
function idLocal() {
  contadorLocal += 1;
  return `compra-local-${contadorLocal}`;
}

/**
 * Registro de una compra nueva (DAF v2, sección 17). Igual que
 * `VentaForm`, todo se envía en una sola llamada al RPC
 * `registrar_compra`, que genera la entrada de inventario de cada línea
 * y — si es a crédito — la cuenta por pagar, de forma atómica en el
 * servidor (supabase/schema_compras.sql).
 */
export function CompraForm() {
  const navigate = useNavigate();
  const { perfil } = useAuth();

  const [proveedores, setProveedores] = useState<ProveedorRow[]>([]);
  const [productos, setProductos] = useState<ProductoRow[]>([]);
  const [formasPago, setFormasPago] = useState<FormaPagoRow[]>([]);

  const [proveedorId, setProveedorId] = useState('');
  const [formaPagoId, setFormaPagoId] = useState('');
  const [esCredito, setEsCredito] = useState(false);
  const [lineas, setLineas] = useState<LineaCompraBorrador[]>([]);

  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      supabase.from('proveedores').select('*').eq('estado', 'activo').order('nombre'),
      supabase.from('productos').select('*').eq('estado', 'activo').order('nombre'),
      supabase.from('formas_pago').select('*').eq('estado', 'activo').order('nombre'),
    ]).then(([p, pr, fp]) => {
      if (p.data) setProveedores(p.data);
      if (pr.data) setProductos(pr.data);
      if (fp.data) setFormasPago(fp.data);
    });
  }, []);

  const total = lineas.reduce((sum, l) => sum + l.cantidad * l.costoUnitario, 0);

  function agregarLinea(linea: Omit<LineaCompraBorrador, 'key'>) {
    setLineas((ls) => [...ls, { ...linea, key: idLocal() }]);
  }

  function quitarLinea(key: string) {
    setLineas((ls) => ls.filter((l) => l.key !== key));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    setError(null);

    if (!proveedorId) {
      setError('Selecciona un proveedor');
      return;
    }
    if (lineas.length === 0) {
      setError('Agrega al menos una línea a la compra');
      return;
    }

    const p_lineas: CompraLineaInput[] = lineas.map((l) => ({
      producto_id: l.productoId,
      cantidad: l.cantidad,
      costo_unitario: l.costoUnitario,
    }));

    setGuardando(true);
    const { error: err } = await supabase.rpc('registrar_compra', {
      p_salon_id: perfil.salon_id,
      p_proveedor_id: proveedorId,
      p_forma_pago_id: formaPagoId || null,
      p_es_credito: esCredito,
      p_lineas,
    });
    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    navigate('/admin/compras');
  }

  return (
    <div className="max-w-3xl">
      <PageHeader title="Nueva compra" />

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="card grid grid-cols-2 gap-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-onyx/80">Proveedor *</span>
            <select required className="input-field" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
              <option value="" disabled>
                Selecciona un proveedor
              </option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-onyx/80">Forma de pago</span>
            <select className="input-field" value={formaPagoId} onChange={(e) => setFormaPagoId(e.target.value)}>
              <option value="">Sin especificar</option>
              {formasPago.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nombre}
                </option>
              ))}
            </select>
          </label>

          <label className="col-span-2 flex items-center gap-2 text-sm text-onyx/80">
            <input type="checkbox" checked={esCredito} onChange={(e) => setEsCredito(e.target.checked)} />
            Es a crédito (genera una cuenta por pagar en vez de descontarse de caja de inmediato)
          </label>
        </div>

        <AgregarLineaCard productos={productos} onAgregar={agregarLinea} />

        <div className="card">
          <p className="mb-2 text-sm font-medium text-onyx/80">Líneas de la compra</p>
          {lineas.length === 0 && <p className="text-sm text-onyx/50">Todavía no has agregado nada.</p>}
          {lineas.length > 0 && (
            <table className="w-full text-sm">
              <tbody>
                {lineas.map((l) => (
                  <tr key={l.key} className="border-b border-onyx/5 last:border-0">
                    <td className="py-2">{l.nombre}</td>
                    <td className="py-2 text-onyx/60">x{l.cantidad}</td>
                    <td className="py-2 text-right text-onyx/70">{COP.format(l.cantidad * l.costoUnitario)}</td>
                    <td className="py-2 pl-3 text-right">
                      <button type="button" className="text-red-600 hover:underline" onClick={() => quitarLinea(l.key)}>
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="mt-3 flex justify-end border-t border-onyx/10 pt-3">
            <p className="font-display text-xl text-gold-deep">Total: {COP.format(total)}</p>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Registrar compra'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/compras')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function AgregarLineaCard({
  productos,
  onAgregar,
}: {
  productos: ProductoRow[];
  onAgregar: (linea: Omit<LineaCompraBorrador, 'key'>) => void;
}) {
  const [productoId, setProductoId] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [costo, setCosto] = useState('0');

  function seleccionarProducto(id: string) {
    setProductoId(id);
    const p = productos.find((i) => i.id === id);
    if (p) setCosto(String(p.costo));
  }

  function handleAgregar() {
    const producto = productos.find((p) => p.id === productoId);
    if (!producto || !cantidad || Number(cantidad) <= 0) return;

    onAgregar({
      productoId,
      nombre: producto.nombre,
      cantidad: Number(cantidad),
      costoUnitario: Number(costo),
    });

    setProductoId('');
    setCantidad('1');
    setCosto('0');
  }

  return (
    <div className="card space-y-3">
      <p className="text-sm font-medium text-onyx/80">Agregar línea</p>
      <div className="grid grid-cols-4 gap-3">
        <select className="input-field col-span-2" value={productoId} onChange={(e) => seleccionarProducto(e.target.value)}>
          <option value="">Selecciona un producto</option>
          {productos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={1}
          step="0.01"
          className="input-field"
          placeholder="Cantidad"
          value={cantidad}
          onChange={(e) => setCantidad(e.target.value)}
        />
        <input
          type="number"
          min={0}
          step={100}
          className="input-field"
          placeholder="Costo unitario"
          value={costo}
          onChange={(e) => setCosto(e.target.value)}
        />
      </div>
      <button type="button" className="btn-secondary" onClick={handleAgregar} disabled={!productoId}>
        Agregar a la compra
      </button>
    </div>
  );
}
